import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { IPredicate, IPredicateGroup, Segment } from '@feathr/blackbox';
import { AlertV2, ConfirmModal, EAlertV2Type, showError, toast } from '@feathr/components';
import type { IUseTableSelectionReturn } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { ARCHIVING_PERSONS, useList } from '@feathr/hooks';

import type { TArchiveAction } from '../types';

import * as styles from './ConfirmArchivePeopleModal.css';

interface IConfirmArchivePeopleModalProps {
  action: TArchiveAction;
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSelectAllArchiveStart?: () => void;
  /** For PersonsTable usage */
  predicates?: Array<IPredicate | IPredicateGroup>;
  selection?: IUseTableSelectionReturn;
  segment?: Segment;
}

function ConfirmArchivePeopleModal({
  action,
  opened,
  onClose,
  onSuccess,
  onSelectAllArchiveStart,
  predicates,
  selection,
  segment,
}: Readonly<IConfirmArchivePeopleModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { Persons } = useStore();
  const archivingList = useList(ARCHIVING_PERSONS);
  const isArchive = action === 'archive';
  const isSelectAllMode = Boolean(selection?.isSelectAllMode);
  const count = isSelectAllMode ? selection?.totalCount ?? 0 : selection?.count ?? 0;
  const isSinglePerson = count === 1;
  const person =
    isSinglePerson && selection?.selected?.[0] ? Persons.get(selection.selected[0]) : undefined;

  const handleConfirm = async (): Promise<void> => {
    try {
      // Mark people as being archived/restored
      if (!isSelectAllMode && selection) {
        const personIds = selection.getSelected().map(String);
        archivingList.add(personIds);
      } else if (isSelectAllMode) {
        // Mark selectAll archive as in progress
        onSelectAllArchiveStart?.();
      }

      if (isSelectAllMode && segment) {
        await (isArchive
          ? Persons.archiveBySegment(segment, predicates)
          : Persons.unarchiveBySegment(segment, predicates));
      } else if (selection) {
        const personIds = selection.getSelected().map(String);
        await (isArchive ? Persons.archiveByIds(personIds) : Persons.unarchiveByIds(personIds));
      }

      onSuccess?.();

      const actionText = isArchive ? 'archive' : 'restore';

      const message = t(
        "You'll be notified when your request to {{action}} {{ personName}} is completed.",
        {
          action: actionText,
          personName: person?.name,
        },
      );

      toast(message, { type: ToastType.INFO });
      selection?.clearSelection();

      onClose();
    } catch (error) {
      // Remove people from archiving list if operation fails
      if (!isSelectAllMode && selection) {
        const personIds = selection.getSelected().map(String);
        archivingList.remove(personIds);
      }
      showError(error, t);
    }
  };

  const actionText = isArchive ? 'Archive' : 'Restore';
  const actionLower = actionText.toLowerCase();

  const modalTitle = isSinglePerson
    ? t(`${actionText} {{personName}}`, { personName: person?.name })
    : t(`${actionText} {{count}} people`, { count });

  const modalDescription = t(
    `You are about to ${actionLower} ${isSinglePerson ? '{{personName}}' : '{{count}} people'}. This action will be processed in the background. You'll receive a notification when the action is complete.`,
    isSinglePerson ? { personName: person?.name } : { count },
  );

  const buttonText = isSinglePerson
    ? t(`${actionText} {{personName}}`, { personName: person?.name })
    : t(`${actionText} {{count}} people`, { count });

  return (
    <ConfirmModal
      confirmationPhrase={isArchive ? 'ARCHIVE' : undefined}
      confirmButtonText={buttonText}
      confirmButtonType={'primary'}
      description={modalDescription}
      onClose={onClose}
      onConfirm={handleConfirm}
      opened={opened}
      t={t}
      title={modalTitle}
    >
      {isArchive && (
        <div className={styles.content}>
          <AlertV2 title={t('Archive from Future Campaigns')} type={EAlertV2Type.warning}>
            <p>
              {isSinglePerson
                ? t(
                    'Archiving {{ personName}} will remove them from all future targeting. This action is best for cleaning up bad or unnecessary records, such as those created from incorrect imports.',
                    { personName: person?.name },
                  )
                : t(
                    'Archiving these people will remove them from all future targeting. This action is best for cleaning up bad or unnecessary records, such as those created from incorrect imports.',
                  )}
            </p>
          </AlertV2>
        </div>
      )}
    </ConfirmModal>
  );
}

export default ConfirmArchivePeopleModal;
